import React, {useEffect, useState} from "react";
import {post, page, primaryBtn, inputStyle} from "./ui";
import {ZedSays} from "./zed";

// The lobby screens while your character exists: the fact form (the host's; guests answer on the join
// screen), then Zed sizing you up while it builds, then your portrait reveal.
// `qr` is passed for the host so guests can join while the host builds theirs.
export const CharacterCreator = ({you, qr, onContinue}) => {
    if (you.character_status === 'none') return <CreatorForm you={you} qr={qr} />;
    return <Becoming you={you} onContinue={onContinue} />;
};

const CreatorForm = ({you, qr}) => {
    const [fact, setFact] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    const submit = async () => {
        if (!fact.trim()) { setErr('Give the Machine one fact'); return; }
        setBusy(true); setErr('');
        const res = await post('/demo/character', {emblem: '', fact: fact.trim()});
        if (!res.ok) { setErr(res.status === 429 ? 'Zed is resting. Try again in a bit.' : 'Something went wrong — try again'); setBusy(false); }
        // On success the parent poll flips status to 'processing'.
    };

    return <div style={{...page, gap: 14}}>
        {qr}
        <ZedSays line={`${you.name}. Tell me something about yourself.`} />
        <input type="text" className="demo-input" style={inputStyle} value={fact} onChange={e => setFact(e.target.value)} placeholder="One interesting fact" />
        <p style={{textAlign: 'right', margin: '2px 0 6px', opacity: 0.7}}><i>e.g.</i> "I once ate 40 tacos"</p>
        {err && <div style={{color: '#ff8a8a', textAlign: 'center', fontWeight: 700}}>{err}</div>}
        <button style={{...primaryBtn, marginTop: 10}} disabled={busy} onClick={submit}>Create Character</button>
    </div>;
};

// What Zed says next, given what's ready and what he's already said: his reaction to your fact (with
// an opener while it's on its way), your nickname, then the reveal (with a filler line if the portrait
// is still rendering).
// Instant, generic, and it sounds like he's already judging what you wrote.
const OPENERS = ['Of course you would put that.', 'Bold of you to admit that.', 'I expected nothing less.',
    'You chose to share that. Interesting.'];
const OPENER = OPENERS[Math.floor(Math.random() * OPENERS.length)];

const nextLine = (you, said) => {
    if (!said.includes('reaction')) {
        if (you.zed_reaction) return {key: 'reaction', text: you.zed_reaction};
        return said.length === 0 ? {key: 'opener', text: OPENER} : null;
    }
    if (!said.includes('nickname')) return you.nickname ? {key: 'nickname', text: `I shall call you... ${you.nickname.toUpperCase()}.`} : null;
    if (!said.includes('welcome')) {
        if (you.character_status === 'ready') return {key: 'welcome', text: "You're ready. Try not to embarrass me."};
        return said.includes('rendering') ? null : {key: 'rendering', text: "I'll show you what you look like."};
    }
    return null;
};

const Becoming = ({you, onContinue}) => {
    const [said, setSaid] = useState([]);
    const [current, setCurrent] = useState(null);
    const [last, setLast] = useState(''); // keeps his last line up while the next one isn't ready yet
    const [typed, setTyped] = useState([]); // lines he's finished typing (they then hold before the next)

    useEffect(() => {
        if (current) return;
        const next = nextLine(you, said);
        if (next) setCurrent(next);
    }, [you, said, current]);

    // Each line holds on screen before the next starts: longer lines hold longer.
    const finished = (line) => {
        setLast(line.text);
        setTyped(t => [...t, line.key]);
        setTimeout(() => { setSaid(s => [...s, line.key]); setCurrent(null); }, 1400 + line.text.length * 35);
    };
    const named = typed.includes('nickname');
    const revealed = said.includes('welcome') || (current && current.key === 'welcome');

    // Like the show's Ready to Play screen: name and nickname stacked simply, the portrait full-bleed below.
    const welcomed = said.includes('welcome') || typed.includes('welcome');
    return <div style={{...page, gap: 18, height: '100dvh', minHeight: 0, overflow: 'hidden', position: 'relative', isolation: 'isolate'}}>
        <div data-fly="fade" style={{position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: -1,
            background: 'radial-gradient(ellipse at top, rgba(54,255,54,0.16) 0%, transparent 60%)',
            opacity: welcomed ? 1 : 0, transition: 'opacity 1200ms ease'}} />
        <ZedSays line={current ? current.text : last} onDone={() => current && finished(current)} />
        <div style={{textAlign: 'center', marginTop: 4}}>
            <div style={{fontSize: 56, fontWeight: 800, lineHeight: 1.0, minHeight: 56}}><TypeOut text={you.name} ms={70} /></div>
            <div style={{fontSize: 32, fontStyle: 'italic', color: '#d9b77a', minHeight: 40, marginTop: 6, lineHeight: 1.1}}>
                {named && <TypeOut text={you.nickname} />}
            </div>
        </div>
        {/* Full width at its natural height, never shrunk to fit: whatever runs past the bottom edge is cut off by it. */}
        <div style={{flex: '1 1 0', minHeight: 0, position: 'relative', overflow: 'hidden',
            margin: onContinue ? '0 -18px' : '0 -18px calc(-24px - env(safe-area-inset-bottom))',
            opacity: revealed ? 1 : 0, transform: revealed ? 'scale(1)' : 'scale(0.88)', transformOrigin: 'center top',
            transition: 'opacity 800ms ease, transform 1000ms cubic-bezier(0.2, 0.9, 0.2, 1.15)'}}>
            {you.portrait_url && <img src={you.portrait_url} alt="" style={{position: 'absolute', top: 0, left: 0, width: '100%', display: 'block'}} />}
        </div>
        {onContinue && <div style={{flexShrink: 0, position: 'relative', opacity: said.includes('welcome') ? 1 : 0, transition: 'opacity 500ms ease 300ms'}}>
            <button style={primaryBtn} onClick={onContinue}>Continue</button>
        </div>}
    </div>;
};

// Types `text` out one letter at a time (the nickname filling into your profile).
const TypeOut = ({text, ms = 60}) => {
    const [n, setN] = useState(0);
    useEffect(() => {
        setN(0);
        const id = setInterval(() => setN(k => (k >= text.length ? (clearInterval(id), k) : k + 1)), ms);
        return () => clearInterval(id);
    }, [text, ms]);
    return <>{text.slice(0, n)}</>;
};
